import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, removeItem, subtotal, savings } = useCart();
  const shipping = subtotal >= 499 ? 0 : subtotal === 0 ? 0 : 99;
  const total = subtotal + shipping;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/40 z-[70]"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.2, 0.7, 0.2, 1], duration: 0.45 }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-white z-[71] flex flex-col"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-black/5">
              <div>
                <div className="overline">Your Bag</div>
                <div className="font-serif-display text-2xl mt-1">{items.length} {items.length === 1 ? "item" : "items"}</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="cart-close" className="p-2">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {items.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center py-20" data-testid="cart-empty-state">
                  <ShoppingBag size={48} strokeWidth={1} className="text-[#C9A961] mb-5" />
                  <div className="font-serif-display text-2xl">Your bag is empty</div>
                  <p className="text-sm text-[#5C5C5C] mt-2 max-w-xs">
                    Discover the salon-edit rituals our editors are loving right now.
                  </p>
                  <Link to="/shop" onClick={() => setOpen(false)} className="btn-outline mt-8" data-testid="cart-explore-shop">
                    Explore Shop
                  </Link>
                </div>
              )}

              {items.map((item) => (
                <div key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-20 h-24 bg-[#F8F5F0] flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="font-serif-display text-base leading-snug">{item.name}</div>
                    <div className="text-sm mt-1 flex items-center gap-2">
                      <span>{formatINR(item.unit_price)}</span>
                      {item.unit_price < item.price && (
                        <span className="text-[10px] tracking-[0.22em] uppercase bg-[#C9A961]/20 text-[#9d8345] px-1.5 py-0.5">
                          Tier
                        </span>
                      )}
                      {item.unit_price < item.price && (
                        <span className="text-xs text-[#5C5C5C] line-through">{formatINR(item.price)}</span>
                      )}
                    </div>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center border border-black/10">
                        <button onClick={() => updateQty(item.product_id, item.quantity - 1)} className="w-7 h-8 hover:bg-[#F8F5F0]" data-testid={`cart-qty-minus-${item.product_id}`}>
                          <Minus size={11} className="mx-auto" />
                        </button>
                        <span className="w-7 text-center text-sm" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                        <button onClick={() => updateQty(item.product_id, item.quantity + 1)} className="w-7 h-8 hover:bg-[#F8F5F0]" data-testid={`cart-qty-plus-${item.product_id}`}>
                          <Plus size={11} className="mx-auto" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.product_id)} className="text-xs underline text-[#5C5C5C]" data-testid={`cart-remove-${item.product_id}`}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-black/5 p-6 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5C5C]">Subtotal</span>
                  <span data-testid="cart-subtotal">{formatINR(subtotal)}</span>
                </div>
                {savings > 0 && (
                  <div className="flex items-center justify-between text-sm text-[#9d8345]" data-testid="cart-savings-row">
                    <span>Bulk savings</span>
                    <span>− {formatINR(savings)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5C5C]">Shipping</span>
                  <span>{shipping === 0 ? "Complimentary" : formatINR(shipping)}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-black/5">
                  <span className="overline">Total</span>
                  <span className="font-serif-display text-2xl" data-testid="cart-total">{formatINR(total)}</span>
                </div>
                <Link
                  to="/checkout"
                  onClick={() => setOpen(false)}
                  className="btn-primary w-full mt-3"
                  data-testid="cart-checkout-button"
                >
                  Proceed to Checkout →
                </Link>
                <button onClick={() => setOpen(false)} className="block text-center w-full overline mt-2 text-[#5C5C5C] hover:text-black" data-testid="cart-continue-shopping">
                  Continue Shopping
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
